'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useTranslations } from '@/i18n/use-translations';
import { EPlatform } from '@/utils/enums/platform-enum';
import { CloseOutlined, SearchOutlined } from '@zeelool/icon';
import { FormEvent, useContext, useEffect, useState } from 'react';

interface BlogSearchInputProps {
  initialValue?: string;
  onSearch?: (keyword: string) => void;
  className?: string;
}

export default function BlogSearchInput({
  initialValue = '',
  onSearch,
  className = '',
}: BlogSearchInputProps) {
  const t = useTranslations('blog.search');
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const [keyword, setKeyword] = useState(initialValue);

  useEffect(() => {
    setKeyword(initialValue);
  }, [initialValue]);

  const trimmedKeyword = keyword.trim();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!trimmedKeyword) return;
    onSearch?.(trimmedKeyword);
  };

  const handleClear = () => {
    setKeyword('');
  };

  const mobileClearIcon = (
    <span aria-hidden className="relative block h-[11.77px] w-[11.77px]">
      <span className="absolute left-1/2 top-1/2 h-[1.38px] w-[13px] -translate-x-1/2 -translate-y-1/2 rotate-45 bg-primary-text" />
      <span className="absolute left-1/2 top-1/2 h-[1.38px] w-[13px] -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-primary-text" />
    </span>
  );

  return (
    <form
      role="search"
      className={`blog-search-input flex w-full items-center border border-[#808080] ${isWeb ? 'h-36 rounded-4 bg-[#f4f4f4]' : 'h-30 rounded-none bg-white pl-[0.3rem] pr-8'} ${className}`}
      onSubmit={handleSubmit}
    >
      <button
        type="submit"
        aria-label={t('search')}
        className={`flex shrink-0 cursor-pointer items-center justify-start border-0 bg-transparent p-0 text-primary-text ${isWeb ? 'ml-8 h-22 w-22' : 'h-17 w-17'}`}
      >
        <SearchOutlined className={isWeb ? 'text-22' : 'text-17'} />
      </button>
      <input
        value={keyword}
        aria-label={t('placeholder')}
        placeholder={t('placeholder')}
        className={`min-w-0 flex-1 border-0 bg-transparent p-0 text-primary-text outline-none placeholder:text-[#666666] ${isWeb ? 'ml-8 font-ir text-12 leading-14' : 'ml-[4.5px] font-ir text-13 font-normal leading-16'}`}
        onChange={(event) => setKeyword(event.target.value)}
      />
      {trimmedKeyword ? (
        <button
          type="button"
          aria-label={t('clear')}
          className={`flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-primary-text ${isWeb ? 'ml-8 mr-8 h-22 w-22' : 'ml-[8.5px] h-[11.77px] w-[11.77px]'}`}
          onClick={handleClear}
        >
          {isWeb ? <CloseOutlined className="text-22" /> : mobileClearIcon}
        </button>
      ) : null}
    </form>
  );
}
